import React, { useState } from "react";
import { AppBar, Toolbar, Button, Box, Container, IconButton, Drawer, Stack, useScrollTrigger } from "@mui/material";
import { NavLink } from "react-router-dom";
import { Wordmark } from "./Wordmark";
import { QuoteButton } from "./QuoteButton";
import { displayFont } from "../styles/theme";

export const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "Sobre" },
  { to: "/videos", label: "Vídeos" },
];

const linkSx = {
  position: "relative",
  minWidth: 0,
  px: 1.5,
  color: "text.secondary",
  fontWeight: 500,
  borderRadius: 0,
  "&::after": {
    content: '""',
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 4,
    height: "1px",
    bgcolor: "primary.main",
    transform: "scaleX(0)",
    transformOrigin: "left",
    transition: "transform 0.3s ease",
  },
  "&:hover": { color: "text.primary", bgcolor: "transparent" },
  "&:hover::after, &.active::after": { transform: "scaleX(1)" },
  "&.active": { color: "text.primary" },
};

function MenuIcon({ open }) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h10" />}
    </svg>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 24 });
  const close = () => setOpen(false);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundImage: "none",
        bgcolor: scrolled ? "rgba(11, 11, 11, 0.72)" : "transparent",
        backdropFilter: scrolled ? "blur(14px)" : "none",
        borderBottom: "1px solid",
        borderColor: scrolled ? "divider" : "transparent",
        transition: "background-color 0.3s ease, border-color 0.3s ease",
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 80 }, justifyContent: "space-between" }}>
          <Wordmark />

          {/* Desktop */}
          <Stack component="nav" direction="row" spacing={1} sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
            {navLinks.map(({ to, label }) => (
              <Button key={to} component={NavLink} to={to} end={to === "/"} sx={linkSx}>
                {label}
              </Button>
            ))}
            <QuoteButton sx={{ ml: 2, px: 3 }} />
          </Stack>

          {/* Mobile */}
          <IconButton
            color="inherit"
            aria-label="Abrir menu"
            onClick={() => setOpen(true)}
            sx={{ display: { xs: "inline-flex", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        open={open}
        onClose={close}
        slotProps={{ paper: { sx: { width: "min(320px, 85vw)", bgcolor: "background.default", backgroundImage: "none", p: 3 } } }}
      >
        <Stack direction="row" sx={{ mb: 6, justifyContent: "space-between", alignItems: "center" }}>
          <Wordmark onClick={close} />
          <IconButton color="inherit" aria-label="Fechar menu" onClick={close}>
            <MenuIcon open />
          </IconButton>
        </Stack>

        <Stack component="nav" spacing={1}>
          {navLinks.map(({ to, label }) => (
            <Box
              key={to}
              component={NavLink}
              to={to}
              end={to === "/"}
              onClick={close}
              sx={{
                py: 1,
                fontFamily: displayFont,
                fontSize: 28,
                color: "text.secondary",
                textDecoration: "none",
                "&.active": { color: "primary.main" },
              }}
            >
              {label}
            </Box>
          ))}
        </Stack>

        <QuoteButton onClick={close} size="large" sx={{ mt: "auto" }} />
      </Drawer>
    </AppBar>
  );
}
